import type {
    InstitutionDto,
    QoqPositionsPage,
    QoqSnapshotDto,
    QoqPeriodSummaryDto,
    InstitutionalHoldingReportDto,
} from "./dtos";

// Very thin axios-like client interface you already use
export interface ApiClient {
    get<T>(url: string, config?: any): Promise<{ data: T }>;
}

const withQuery = (base: string, params: Record<string, any>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) v.forEach((x) => q.append(k, x));
        else q.append(k, String(v));
    });
    const qs = q.toString();
    return qs ? `${base}?${qs}` : base;
};

// ----- Institutions -----
export async function listInstitutions(api: ApiClient): Promise<InstitutionDto[]> {
    const { data } = await api.get<InstitutionDto[]>("/institutions");
    return data ?? [];
}

// ----- QoQ periods -----
export async function listQoqPeriods(
    api: ApiClient,
    institutionId: number,
    from?: string,
    to?: string
): Promise<QoqPeriodSummaryDto[]> {
    const url = withQuery(`/institutions/${institutionId}/qoq`, { from, to });
    const { data } = await api.get<QoqPeriodSummaryDto[]>(url);
    return data ?? [];
}

export async function getQoqById(api: ApiClient, id: number): Promise<QoqSnapshotDto> {
    const { data } = await api.get<QoqSnapshotDto>(`/qoq/${id}`);
    return data;
}

// ----- Reports -----
export async function getReportById(api: ApiClient, reportId: number): Promise<InstitutionalHoldingReportDto> {
    const { data } = await api.get<InstitutionalHoldingReportDto>(`/reports/${reportId}`);
    return data;
}

// ----- QoQ positions -----
export async function listQoqPositionsBySnapshot(
    api: ApiClient,
    snapshotId: number,
    args: { limit?: number; sort?: "VALUE_DESC" | "ID_ASC"; cursor?: string | null; tickers?: string[] }
): Promise<QoqPositionsPage> {
    const url = withQuery(`/qoq/${snapshotId}/positions`, {
        limit: args.limit,
        sort: args.sort,
        cursor: args.cursor,
        ...(args.tickers && args.tickers.length ? { tickers: args.tickers } : {}),
    });
    const { data } = await api.get<QoqPositionsPage>(url);
    return data;
}

export async function listQoqPositionsByTickerRange(
    api: ApiClient,
    ticker: string,
    args: {
        from?: string;
        to?: string;
        limit?: number;
        sort?: "VALUE_DESC" | "ID_ASC";
        cursor?: string | null;
    }
): Promise<QoqPositionsPage> {
    const url = withQuery(`/tickers/${encodeURIComponent(ticker)}/qoq/positions`, {
        from: args.from,
        to: args.to,
        limit: args.limit,
        sort: args.sort,
        cursor: args.cursor,
    });
    const { data } = await api.get<QoqPositionsPage>(url);
    return data;
}
